import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Calendar, Clock, FileText, Plus, Trash2, Loader2, Mail, CalendarDays, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScheduledReportsManager({ userRole = 'DRLP' }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [formData, setFormData] = useState({
    report_type: 'retailer_summary',
    frequency: 'weekly',
    delivery_time: '08:00',
    delivery_day: '0',
    include_charts: true,
    format: 'pdf'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const createSchedule = async () => {
    setCreateLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          delivery_day: parseInt(formData.delivery_day)
        })
      });

      if (res.ok) {
        toast.success('Report schedule created!');
        setShowCreateModal(false);
        setFormData({
          report_type: 'retailer_summary',
          frequency: 'weekly',
          delivery_time: '08:00',
          delivery_day: '0',
          include_charts: true,
          format: 'pdf'
        });
        fetchSchedules();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create schedule');
      }
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error('Failed to create schedule');
    } finally {
      setCreateLoading(false);
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/${scheduleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Schedule deleted');
        fetchSchedules();
      } else {
        toast.error('Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const formatFrequency = (freq) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    };
    return labels[freq] || freq;
  };

  const formatReportType = (type) => {
    const labels = {
      retailer_summary: 'Performance Summary',
      retailer_top_items: 'Top Items Report',
      admin_summary: 'Platform Summary',
      admin_demand: 'Demand Analysis'
    };
    return labels[type] || type;
  };

  const getDayLabel = (freq, day) => {
    if (freq === 'weekly') {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return days[day] || 'Monday';
    }
    if (freq === 'monthly') {
      return `Day ${day}`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Scheduled Reports</h2>
          <p className="text-gray-500">Receive automated analytics reports via email</p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Schedule a Report</DialogTitle>
              <DialogDescription>
                Configure automated report delivery to your email
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select 
                  value={formData.report_type} 
                  onValueChange={(v) => setFormData({ ...formData, report_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retailer_summary">Performance Summary</SelectItem>
                    <SelectItem value="retailer_top_items">Top Items Report</SelectItem>
                    {userRole === 'Admin' && (
                      <>
                        <SelectItem value="admin_summary">Platform Summary</SelectItem>
                        <SelectItem value="admin_demand">Demand Analysis</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select 
                    value={formData.delivery_day} 
                    onValueChange={(v) => setFormData({ ...formData, delivery_day: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Monday</SelectItem>
                      <SelectItem value="1">Tuesday</SelectItem>
                      <SelectItem value="2">Wednesday</SelectItem>
                      <SelectItem value="3">Thursday</SelectItem>
                      <SelectItem value="4">Friday</SelectItem>
                      <SelectItem value="5">Saturday</SelectItem>
                      <SelectItem value="6">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Day of Month</Label>
                  <Select 
                    value={formData.delivery_day} 
                    onValueChange={(v) => setFormData({ ...formData, delivery_day: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 5, 10, 15, 20, 25, 28].map((day) => (
                        <SelectItem key={day} value={day.toString()}>Day {day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Delivery Time</Label>
                <Select 
                  value={formData.delivery_time} 
                  onValueChange={(v) => setFormData({ ...formData, delivery_time: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="06:00">6:00 AM</SelectItem>
                    <SelectItem value="08:00">8:00 AM</SelectItem>
                    <SelectItem value="09:00">9:00 AM</SelectItem>
                    <SelectItem value="12:00">12:00 PM</SelectItem>
                    <SelectItem value="17:00">5:00 PM</SelectItem>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select 
                    value={formData.format} 
                    onValueChange={(v) => setFormData({ ...formData, format: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF with Charts</SelectItem>
                      <SelectItem value="csv">CSV (Data Only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Include Charts</Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      checked={formData.include_charts}
                      onCheckedChange={(checked) => setFormData({ ...formData, include_charts: checked })}
                      disabled={formData.format === 'csv'}
                    />
                    <span className="text-sm text-gray-600">
                      {formData.include_charts ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button 
                onClick={createSchedule} 
                disabled={createLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calendar className="h-4 w-4 mr-2" />}
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No scheduled reports</p>
              <p className="text-sm">Set up automated reports to receive analytics via email</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {schedules.map((schedule) => (
            <Card key={schedule.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      schedule.format === 'pdf' ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      <FileText className={`h-6 w-6 ${
                        schedule.format === 'pdf' ? 'text-red-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{formatReportType(schedule.report_type)}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-4 w-4" />
                          {formatFrequency(schedule.frequency)}
                          {schedule.frequency !== 'daily' && ` - ${getDayLabel(schedule.frequency, schedule.delivery_day)}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {schedule.delivery_time}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="uppercase text-xs">
                          {schedule.format}
                        </Badge>
                        {schedule.include_charts && (
                          <Badge variant="secondary" className="text-xs">
                            With Charts
                          </Badge>
                        )}
                        <Badge 
                          variant={schedule.status === 'active' ? 'default' : 'secondary'}
                          className={schedule.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}
                        >
                          {schedule.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-right text-sm mr-4">
                      <p className="text-gray-500">
                        {schedule.delivery_count || 0} sent
                      </p>
                      {schedule.next_delivery && (
                        <p className="text-xs text-gray-400">
                          Next: {new Date(schedule.next_delivery).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteSchedule(schedule.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
